import { useCallback, useEffect, useRef, useState } from 'react';
import type { ThemePreference } from './useTheme';

/**
 * App identifiers known to the admin theme settings page. Keep this list
 * in sync with `APP_IDS` in packages/api/src/routes/admin-config.ts so the
 * server and clients agree on the keys.
 */
export const APP_IDS = ['landing-page', 'flow-builder', 'admin-console', 'admin-logs', 'admin-settings'] as const;
export type AppId = (typeof APP_IDS)[number];

const VALID: readonly ThemePreference[] = ['system', 'light', 'dark'] as const;

/** Storage key for the **per-user override** of an app's theme. */
function userKey(appId: AppId): string {
  return `rynora-theme:${appId}`;
}

/** Storage key for the **admin default** snapshot we last fetched. Used so
 *  the next page load can paint the right theme synchronously, before the
 *  /api/theme/apps round-trip completes. */
function adminCacheKey(appId: AppId): string {
  return `rynora-theme-admin:${appId}`;
}

function isValidPref(v: unknown): v is ThemePreference {
  return typeof v === 'string' && (VALID as readonly string[]).includes(v);
}

function readStorage(key: string): ThemePreference | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    return isValidPref(raw) ? (raw as ThemePreference) : null;
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: ThemePreference | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch {
    // Ignore — Safari private mode, sandboxed iframes, etc.
  }
}

function applyToDocument(pref: ThemePreference): void {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  if (pref === 'system') {
    html.removeAttribute('data-theme');
  } else {
    html.setAttribute('data-theme', pref);
  }
}

function readSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

/**
 * Resolves the effective preference for an app at boot:
 *   user override (per browser) > admin default (cached or freshly fetched) > 'system'
 */
function resolveInitial(appId: AppId): { pref: ThemePreference; source: AppThemeSource } {
  const user = readStorage(userKey(appId));
  if (user) return { pref: user, source: 'user' };
  const admin = readStorage(adminCacheKey(appId));
  if (admin) return { pref: admin, source: 'admin' };
  return { pref: 'system', source: 'fallback' };
}

export type AppThemeSource = 'user' | 'admin' | 'fallback';

export interface UseAppThemeOptions {
  /** Base URL of the Rynora API (defaults to `http://localhost:3001`). */
  apiBaseUrl?: string;
  /** Skip the network fetch (e.g. in unit tests). */
  skipFetch?: boolean;
}

export interface UseAppThemeResult {
  /** Current effective preference. */
  theme: ThemePreference;
  /** Where the current preference came from. */
  source: AppThemeSource;
  /** Whether the user has set a per-app override that wins over the admin default. */
  hasUserOverride: boolean;
  /** Resolved colour mode actually painted (`light` or `dark`). */
  resolved: 'light' | 'dark';
  /** Set a per-user override and persist to localStorage. */
  setTheme: (next: ThemePreference) => void;
  /** Drop the per-user override and follow the admin default again. */
  clearOverride: () => void;
}

/**
 * Per-app theme hook. Each Rynora React app calls this once at the root
 * to apply the admin-controlled default mode (with a per-user override
 * via the ThemeToggle component).
 *
 *   const { theme, setTheme } = useAppTheme('flow-builder');
 *
 * The hook is cheap and tab-synced (storage event), and it tolerates
 * server unreachability gracefully — the cached admin default + `system`
 * fallback mean the page never blocks on the network.
 */
export function useAppTheme(appId: AppId, options: UseAppThemeOptions = {}): UseAppThemeResult {
  const { apiBaseUrl = 'http://localhost:3001', skipFetch = false } = options;

  const initial = resolveInitial(appId);
  const [theme, setThemeState] = useState<ThemePreference>(initial.pref);
  const [source, setSource] = useState<AppThemeSource>(initial.source);
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => {
    if (initial.pref === 'light') return 'light';
    if (initial.pref === 'dark') return 'dark';
    return readSystemTheme();
  });

  // Latest user-override flag; lets the API-fetch effect skip applying
  // an admin default if the user has already chosen something explicit.
  const hasUserOverrideRef = useRef<boolean>(initial.source === 'user');
  const [hasUserOverride, setHasUserOverride] = useState<boolean>(initial.source === 'user');

  // Apply current pref to <html> + watch system changes when `system`.
  useEffect(() => {
    applyToDocument(theme);
    if (theme === 'light' || theme === 'dark') {
      setResolved(theme);
      return;
    }
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      setResolved(readSystemTheme());
      return;
    }
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setResolved(mql.matches ? 'dark' : 'light');
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, [theme]);

  // Fetch admin default (one-shot on mount). Updates the cached admin
  // default in localStorage so the next reload paints correctly without
  // waiting for the network.
  useEffect(() => {
    if (skipFetch) return;
    if (typeof window === 'undefined' || typeof fetch !== 'function') return;
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), 4000);
    (async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/theme/apps`, { signal: ctrl.signal });
        if (!res.ok) return;
        const data = (await res.json()) as { apps?: Record<string, string> };
        const adminPref = data?.apps?.[appId];
        if (!isValidPref(adminPref)) return;
        writeStorage(adminCacheKey(appId), adminPref);
        if (!hasUserOverrideRef.current) {
          setThemeState(adminPref);
          setSource('admin');
        }
      } catch {
        // Network/CORS/abort — stay on cached or fallback. No-op.
      } finally {
        window.clearTimeout(timer);
      }
    })();
    return () => {
      ctrl.abort();
      window.clearTimeout(timer);
    };
  }, [appId, apiBaseUrl, skipFetch]);

  // Cross-tab sync: react to changes in either the per-user override
  // or the admin cache. (Other tabs of the same app may have toggled.)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== userKey(appId) && e.key !== adminCacheKey(appId)) return;
      const { pref, source: src } = resolveInitial(appId);
      setThemeState(pref);
      setSource(src);
      hasUserOverrideRef.current = src === 'user';
      setHasUserOverride(src === 'user');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [appId]);

  const setTheme = useCallback(
    (next: ThemePreference) => {
      if (!isValidPref(next)) return;
      writeStorage(userKey(appId), next);
      setThemeState(next);
      setSource('user');
      hasUserOverrideRef.current = true;
      setHasUserOverride(true);
    },
    [appId],
  );

  const clearOverride = useCallback(() => {
    writeStorage(userKey(appId), null);
    hasUserOverrideRef.current = false;
    setHasUserOverride(false);
    const cached = readStorage(adminCacheKey(appId));
    if (cached) {
      setThemeState(cached);
      setSource('admin');
    } else {
      setThemeState('system');
      setSource('fallback');
    }
  }, [appId]);

  return { theme, source, hasUserOverride, resolved, setTheme, clearOverride };
}

/**
 * Inline this string in <head> BEFORE the React bundle loads to avoid a
 * theme flash. Apply the per-app `data-theme` synchronously by reading
 * the user override first, then the cached admin default.
 *
 * Usage in index.html:
 *
 *   <script>{noFlashAppThemeScript('flow-builder')}</script>
 */
export function noFlashAppThemeScript(appId: AppId): string {
  return `(function(){try{var u=localStorage.getItem('rynora-theme:${appId}');var a=localStorage.getItem('rynora-theme-admin:${appId}');var t=u||a;if(t==='light'||t==='dark'){document.documentElement.setAttribute('data-theme',t);}}catch(e){}})();`;
}

/**
 * Synchronously read the cached admin default for an app from
 * localStorage. Returns `null` if no cache exists yet or if the value
 * is not a recognised theme mode.
 *
 * Useful for apps (like flow-builder) that own their own theme state
 * and just want to consult the cached admin default during state init.
 */
export function readCachedAdminAppTheme(appId: AppId): ThemePreference | null {
  return readStorage(adminCacheKey(appId));
}

/**
 * Read the per-user override an app's ThemeToggle has stored.
 * Returns `null` if the user hasn't set an explicit preference.
 */
export function readUserAppTheme(appId: AppId): ThemePreference | null {
  return readStorage(userKey(appId));
}

/**
 * Fire-and-forget fetch that asks the API for the admin's per-app
 * theme map and writes the slot for `appId` into the local admin
 * cache so the next page load can paint synchronously.
 *
 * Apps that own their own theme state (e.g. flow-builder) call this
 * once at boot. The promise resolves to the freshly-fetched mode (or
 * `null` on network failure) so the caller can also act on it now.
 */
export async function fetchAndCacheAdminAppTheme(
  appId: AppId,
  apiBaseUrl = 'http://localhost:3001',
): Promise<ThemePreference | null> {
  if (typeof fetch !== 'function') return null;
  try {
    const ctrl = new AbortController();
    const timer = typeof window !== 'undefined' ? window.setTimeout(() => ctrl.abort(), 4000) : null;
    const res = await fetch(`${apiBaseUrl}/api/theme/apps`, { signal: ctrl.signal });
    if (timer !== null && typeof window !== 'undefined') window.clearTimeout(timer);
    if (!res.ok) return null;
    const data = (await res.json()) as { apps?: Record<string, string> };
    const adminPref = data?.apps?.[appId];
    if (!isValidPref(adminPref)) return null;
    writeStorage(adminCacheKey(appId), adminPref);
    return adminPref;
  } catch {
    return null;
  }
}
